import type {
  ChannelBadgeStatus,
  ConnectionStatus,
} from "../constants/connectionStatus.constants";
import type { RequestStatus } from "../constants/connectionHub.constants";

export const AUTH_METHOD = {
  OAUTH: "oauth",
  PASSWORD: "password",
  EMAIL_OTP: "email_otp",
  OTP: "otp",
  MAGIC_LINK: "magic_link",
  EMAIL_OTP_PASSWORD: "email_otp_password",
} as const;
export type AuthMethod = (typeof AUTH_METHOD)[keyof typeof AUTH_METHOD];

export const CHANNEL_SECTION = { QUICK: "quick", SMART: "smart" } as const;
export type ChannelSection =
  (typeof CHANNEL_SECTION)[keyof typeof CHANNEL_SECTION];

export const CREDENTIAL_FIELD = { EMAIL: "email", PASSWORD: "password" } as const;
export type CredentialField =
  (typeof CREDENTIAL_FIELD)[keyof typeof CREDENTIAL_FIELD];

export const OTP_MODE = {
  NONE: "none",
  EMAIL: "email",
  MOBILE: "mobile",
} as const;
export type OtpMode = (typeof OTP_MODE)[keyof typeof OTP_MODE];

export const INBOX_PROVIDER = {
  GMAIL: "gmail",
  OUTLOOK: "outlook",
  ZOHO: "zoho",
  OTHERS: "others",
  UNKNOWN: "unknown",
} as const;
export type InboxProvider =
  (typeof INBOX_PROVIDER)[keyof typeof INBOX_PROVIDER];

export const PROVIDER_SOURCE = { AUTO: "auto", USER: "user" } as const;
export type ProviderSource =
  (typeof PROVIDER_SOURCE)[keyof typeof PROVIDER_SOURCE];

export const INBOX_OWNER = { OWN: "own", DELEGATE: "delegate" } as const;
export type InboxOwner = (typeof INBOX_OWNER)[keyof typeof INBOX_OWNER];

export const FORWARDING_STATUS = {
  REQUIRED: "required",
  CONFIRMING: "confirming",
  CONFIRMED: "confirmed",
} as const;
export type ForwardingStatus =
  (typeof FORWARDING_STATUS)[keyof typeof FORWARDING_STATUS];

export const VERIFICATION_MODE = { AUTO: "auto", MANUAL_OPS: "manual_ops" } as const;
export type VerificationMode =
  (typeof VERIFICATION_MODE)[keyof typeof VERIFICATION_MODE];

export const POST_VERIFY_STEP = { SUB_BRANDS: "subBrands" } as const;
export type PostVerifyStep =
  (typeof POST_VERIFY_STEP)[keyof typeof POST_VERIFY_STEP];

export const SETUP_START_STEP = {
  CREDENTIALS: "credentials",
  FINISH_SETUP: "finishSetup",
  DELEGATED_VIEW: "delegatedView",
  EDIT: "edit",
} as const;
export type SetupStartStep =
  (typeof SETUP_START_STEP)[keyof typeof SETUP_START_STEP];

export const CONNECTION_HUB_MODE = {
  SINGLE: "single",
  MULTI: "multi",
} as const;
export type ConnectionHubMode =
  (typeof CONNECTION_HUB_MODE)[keyof typeof CONNECTION_HUB_MODE];

export const CONNECTION_HUB_CLOSE_REASON = {
  CLOSE_BUTTON: "close-button",
  BACKDROP: "backdrop",
  ESCAPE: "escape",
  PROGRAMMATIC: "programmatic",
  REPLACED: "replaced",
  COMPLETED: "completed",
  ACCOUNT_LIST: "account-list",
} as const;
export type ConnectionHubCloseReason =
  (typeof CONNECTION_HUB_CLOSE_REASON)[keyof typeof CONNECTION_HUB_CLOSE_REASON];

export const FAILURE_REASON = {
  CREDENTIALS: "credentials",
  FILTER: "filter",
  FORWARDING: "forwarding",
  VERIFICATION: "verification",
  UNKNOWN: "unknown",
} as const;
export type FailureReason =
  (typeof FAILURE_REASON)[keyof typeof FAILURE_REASON];

export const INBOX_ACTION = {
  OPEN_FORWARDING_SETTINGS: "OPEN_FORWARDING_SETTINGS",
  CONFIRM_FORWARDING: "CONFIRM_FORWARDING",
  OPEN_FILTER_SETTINGS: "OPEN_FILTER_SETTINGS",
  CONFIRM_FILTER: "CONFIRM_FILTER",
  COPY_LINK: "COPY_LINK",
  VERIFY: "VERIFY",
  RESEND_LINK: "RESEND_LINK",
} as const;
export type InboxAction = (typeof INBOX_ACTION)[keyof typeof INBOX_ACTION];

export const ACCOUNT_ACTION = {
  EDIT: "EDIT",
  REMOVE: "REMOVE",
  RETRY_VERIFY: "RETRY_VERIFY",
  CONTINUE_SETUP: "CONTINUE_SETUP",
  RESEND_LINK: "RESEND_LINK",
  SELECT_SUB_BRANDS: "SELECT_SUB_BRANDS",
} as const;
export type AccountAction =
  (typeof ACCOUNT_ACTION)[keyof typeof ACCOUNT_ACTION];

export const FIX_ACTION = {
  EDIT_CHANNEL: "EDIT_CHANNEL",
  RECHECK_INBOX: "RECHECK_INBOX",
  RETRY_VERIFY: "RETRY_VERIFY",
  RESEND_LINK: "RESEND_LINK",
} as const;
export type FixAction = (typeof FIX_ACTION)[keyof typeof FIX_ACTION];

export const REVERIFY_SCOPE = { CHANNEL: "CHANNEL", INBOX: "INBOX" } as const;
export type ReverifyScope =
  (typeof REVERIFY_SCOPE)[keyof typeof REVERIFY_SCOPE];

export interface ApiSuccess<T> {
  success: true;
  data: T;
}

export interface ApiError {
  success: false;
  errorCode: string;
  message: string;
  details?: unknown;
  statusCode: number;
  time: string;
}

export type ApiResponse<T> = ApiSuccess<T> | ApiError;

export interface ConnectionHubError {
  code: string;
  message: string;
  statusCode?: number;
  details?: unknown;
  retryable: boolean;
}

export interface ProviderHelpSteps {
  forwarding: string[];
  filter: string[];
}

export interface ChannelBadge {
  status: ChannelBadgeStatus;
  count: number;
  accountCount?: number;
}

export interface ChannelCapability {
  authMethod: AuthMethod;
  fields: CredentialField[];
  otpModes: OtpMode[];
  defaultOtpMode: OtpMode | null;
  needsInbox: boolean;
  verificationMode: VerificationMode;
  postVerifyStep: PostVerifyStep | null;
  fillOrder: number;
  helpSteps: Partial<Record<InboxProvider, ProviderHelpSteps>>;
  helpVideoUrl: string | null;
}

export interface ChannelCatalogItem {
  ipId: string;
  ipName: string;
  logo: string | null;
  section: ChannelSection;
  isSuggested: boolean;
  badge: ChannelBadge;
  capability: ChannelCapability;
}

export interface ChannelCatalogSection {
  section: ChannelSection;
  channels: ChannelCatalogItem[];
}

export interface ChannelCatalogResponse {
  sections: ChannelCatalogSection[];
}

export interface DraftView {
  connectionId: string;
  ipId: string;
  ipName: string;
  status: ConnectionStatus;
  loginEmail: string | null;
  otpMode: OtpMode | null;
  inboxOwner: InboxOwner | null;
  hasPassword: boolean;
}

export interface PublicConnection {
  connectionId: string;
  ipId: string;
  ipName: string;
  status: ConnectionStatus;
  failureReason: FailureReason | null;
  loginEmail: string | null;
  otpMode: OtpMode | null;
  inboxId: string | null;
  hasPassword: boolean;
  pendingSubBrands: SubBrand[];
  selectedSubBrands: SubBrand[];
  connectedAt: string | null;
}

export interface InboxView {
  id: string;
  email: string;
  provider: InboxProvider;
  providerSource: ProviderSource;
  ownerType: InboxOwner;
  forwardingStatus: ForwardingStatus;
  forwardingError: string | null;
  forwardingCheckedAt: string | null;
}

export interface CoverView {
  connectionId: string;
  ipId: string;
  ipName: string;
  logo: string | null;
  otpMode: OtpMode | null;
  hasPassword: boolean;
  /** Whether the channel's login takes a password at all; OTP-only channels never do. Optional for older API responses. */
  usesPassword?: boolean;
  status: ConnectionStatus;
  actions: InboxAction[];
  ownerAdjusted: boolean;
  failureReason: FailureReason | null;
  siblingConnected: boolean;
  lastError: string | null;
  fixPath: FixPath | null;
  pendingSubBrands: SubBrand[];
  selectedSubBrands: SubBrand[];
}

export interface InboxLinks {
  forwardingSettings: string | null;
  filterSettings: string | null;
  hasWords?: boolean;
  filterFrom?: string | null;
  filterHasWords?: string | null;
}

/**
 * A mail rule the user creates by hand, for providers with no prefill deep link. Empty for Gmail,
 * whose criteria ride in the create-filter URL instead.
 */
export interface MailRule {
  coveredIpIds: string[];
  name: string;
  field?: "from" | "hasWords";
  conditionLabel: string;
  conditionValues: string[];
  /** Outlook ANDs a subject/body condition, so each value is entered on its own line. */
  valuesAddedSeparately: boolean;
  redirectTo: string;
}

/** Public delegate page header data; each field is null when the backend cannot resolve it. */
export interface DelegateInboxMeta {
  inviterName: string | null;
  brandName: string | null;
  expiresAt: string | null;
}

export interface InboxGroup {
  inbox: InboxView;
  covers: CoverView[];
  status: ChannelBadgeStatus;
  actions: InboxAction[];
  forwardingAddress: string;
  links: InboxLinks;
  rules: MailRule[];
}

export interface DelegateInboxResponse extends InboxGroup {
  delegation?: DelegateInboxMeta;
}

export interface StandaloneCard {
  connection: PublicConnection;
  status: ConnectionStatus;
  actions: InboxAction[];
  /** What went wrong, worded for the user; null unless the card failed. Optional for older API responses. */
  lastError?: string | null;
}

export interface SetupResponse {
  inboxes: InboxGroup[];
  standalone: StandaloneCard[];
}

export interface ResolveResult extends SetupResponse {
  startStep: SetupStartStep;
  channelsNeedingCredentials: string[];
  drafts: DraftView[];
  editContext: ResolveEditContext | null;
}

export type ResolveEditContext =
  | { type: "credentials"; connectionId: string }
  | { type: "inboxRecheck"; connectionId: string; inboxId: string };

export interface DelegationView {
  inboxId: string;
  inboxEmail: string;
  status: ConnectionStatus;
  expiresAt: string | null;
  lastSentAt: string | null;
  sendCount: number;
  shareUrl: string | null;
}

export interface EmailSuggestions {
  loginEmail: string | null;
  emails: string[];
}

export interface AccountInboxSummary {
  id: string;
  email: string;
  provider: InboxProvider;
  ownerType: InboxOwner;
  forwardingStatus: ForwardingStatus;
}

export interface FixPath {
  action: FixAction;
  reverify: ReverifyScope | null;
  inboxId: string | null;
}

export interface AccountRow {
  connection: PublicConnection;
  status: ConnectionStatus;
  inbox: AccountInboxSummary | null;
  hasPendingSubBrands: boolean;
  lastError: string | null;
  actions: AccountAction[];
  fixPath: FixPath | null;
}

export interface SubBrandProperty {
  id: string;
  name: string;
}

export interface SubBrand {
  id: string;
  name: string;
  parentId?: string;
  parentName?: string;
  properties?: SubBrandProperty[];
}

export interface SocketConnectionUpdate {
  id: string;
  ipId: string;
  status: ConnectionStatus;
  failureReason: FailureReason | null;
}

export interface ConnectionSocketPayload {
  companyId: number;
  connections: SocketConnectionUpdate[];
  inbox?: InboxView;
  channelBadges: Array<{ ipId: string } & ChannelBadge>;
  inboxGroups?: InboxGroup[];
  standalone?: StandaloneCard[];
}

export interface RemoveDraftResult {
  deleted: boolean;
  keptReason?: "hasData";
}

export interface RemoveConnectionResult {
  removed: boolean;
  disconnectedTokenIds: number[];
}

export interface ConnectionInput {
  ipId: string;
  email?: string;
  password?: string;
  otpMode?: OtpMode;
  inboxOwner?: InboxOwner;
}

export interface AgencyContextInput {
  agencyClientId?: number | null;
}

export interface SaveDraftInput extends AgencyContextInput {
  channels: ConnectionInput[];
}

export interface SetupInput extends AgencyContextInput {
  channels: ConnectionInput[];
}

export interface UpdateCredentialsInput extends AgencyContextInput {
  email?: string;
  password?: string;
  otpMode?: OtpMode;
  inboxOwner?: InboxOwner;
}

/** Narrows confirm/verify to part of an inbox, e.g. one Outlook rule. Omitted means the whole inbox. */
export interface ConnectionScopeInput extends AgencyContextInput {
  connectionIds?: string[];
}

export interface SaveSubBrandsInput extends AgencyContextInput {
  subBrands: Array<Pick<SubBrand, "id" | "name" | "properties">>;
}

export interface ConnectionHubOpenOptions {
  ipIds: string[];
  mode: ConnectionHubMode;
  source?: string;
  agencyClientId?: number | null;
  connectionId?: string;
  startNew?: boolean;
  onSuccess?: (result: ConnectionHubSuccessResult) => void;
  onClose?: (event: ConnectionHubCloseEvent) => void;
}

export interface ConnectionHubSuccessResult {
  mode: ConnectionHubMode;
  ipIds: string[];
  setup: SetupResponse;
}

export interface ConnectionHubCloseEvent {
  reason: ConnectionHubCloseReason;
  ipId?: string;
}

/** A quick-connect channel, as the legacy integration-partner catalogue describes it. */
export interface QuickConnectLaunch {
  _id?: string;
  companyName?: string;
  logo?: string | null;
}

export interface ConnectionHubContextValue {
  open: (options: ConnectionHubOpenOptions) => void;
  /** Opens the quick-connect account list. The redirect happens from inside it, not on the click. */
  openQuick: (target: QuickConnectLaunch) => void;
  close: () => void;
  refreshConnectionChannels: () => Promise<void>;
  isOpen: boolean;
}

export interface RequestState {
  status: RequestStatus;
  error: ConnectionHubError | null;
}

export interface ConnectionHubState {
  session: {
    isOpen: boolean;
    source: string | null;
    agencyClientId: number | null;
    mode: ConnectionHubMode | null;
    selectedIpIds: string[];
    activeIpId: string | null;
  };
  catalog: {
    data: ChannelCatalogResponse | null;
    status: RequestStatus;
    error: ConnectionHubError | null;
  };
  accountsByIpId: Record<string, AccountRow[]>;
  accountRequestsByIpId: Record<string, RequestState>;
  emailSuggestions: {
    data: EmailSuggestions | null;
    status: RequestStatus;
    error: ConnectionHubError | null;
  };
  socket: {
    revision: number;
    latest: ConnectionSocketPayload | null;
  };
}
